import { useEffect, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import type { BillOut } from "../../../../api/bills";
import { useAppDispatch } from "../../../../store/hooks";
import { setSelectedBillId } from "../../../../store";

interface Props {
  /** Отговорът на `GET /bills`, от който са `sortedBills` (данните на `useBills`). */
  bills: BillOut[];
  /** Масите в реда на списъка (`useTableListBills` → `sortedBills`). */
  sortedBills: BillOut[];
  selectedBillId: number | null;
  isTablesView: boolean;
  /** false (режим „Схема") — изборът е празен или на свободна маса: изчезналата
   *  сметка само се маха, без да се скача на първата маса. */
  autoSelectFirst: boolean;
}

/**
 * Авто-изборът на първата маса, щом избраната вече я няма (в режим „Схема" —
 * само махането на изчезналата сметка). Изчаква опресняването на сметките
 * (напр. след нова маса), за да не скочи на първата, преди новата да дойде.
 *
 * Отделен компонент (рисува нищо), за да е абонаментът за „сметките се
 * зареждат" ТУК, а не в `Ordering`: там всяко опресняване — и с еднакви данни
 * — прерисуваше целия екран два пъти (начало и край на заявката).
 *
 * Изчаква и ПЪРВОТО зареждане след монтирането: `useIsFetching` брои само
 * тръгналите заявки, а заявката при монтиране тръгва в ефекта на `Ordering` —
 * СЛЕД ефекта тук. Със стар кеш (избраната сметка я няма: „Настани" от
 * „Резервации", връщане след 5+ мин в Справки) изборът би скочил на първата
 * маса, преди сметката да дойде. Затова — нищо, докато сметките не са дошли
 * (или паднали) след монтирането. При бавно меню това е четенето, което
 * `Ordering` пуска, щом масите се покажат (useOrderingBillsRefresh). НЕ с втори
 * `useBills()`: управляващият абонат е само `Ordering` — всеки друг, монтиран
 * след края на заявка, пуска собствен GET /bills с току-що дошлите данни.
 *
 * Края на заявката (1→0) този компонент го вижда ПРЕДИ `Ordering` да е
 * прерисувал списъка с новите сметки: `sortedBills` още е без току-що
 * поръчаната нова маса и изборът скачаше на първата. Затова — нищо, докато
 * `bills` (данните на родителя) не са същите като в кеша.
 */
export function TablesAutoSelect({ bills, sortedBills, selectedBillId, isTablesView, autoSelectFirst }: Props) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [mountedAt] = useState(() => Date.now());
  // Смяната 1→0 прерисува компонента — тогава и състоянието по-долу е прясно
  const billsFetching = useIsFetching({ queryKey: ["bills"], exact: true }) > 0;
  const billsState = queryClient.getQueryState(["bills"]);
  const loadedSinceMount =
    billsState != null && Math.max(billsState.dataUpdatedAt, billsState.errorUpdatedAt) >= mountedAt;

  useEffect(() => {
    if (!isTablesView || billsFetching || !loadedSinceMount) return;
    const cachedBills = queryClient.getQueryData<BillOut[]>(["bills"]);
    if (cachedBills !== undefined && cachedBills !== bills) return;

    if (sortedBills.length === 0) {
      if (selectedBillId !== null) dispatch(setSelectedBillId(null));
      return;
    }

    // Първата маса — само ако няма валиден избор
    if (sortedBills.some((bill) => bill.id === selectedBillId)) return;
    if (autoSelectFirst) dispatch(setSelectedBillId(sortedBills[0].id));
    else if (selectedBillId !== null) dispatch(setSelectedBillId(null));
  }, [isTablesView, bills, sortedBills, selectedBillId, dispatch, queryClient, billsFetching, loadedSinceMount, autoSelectFirst]);

  return null;
}
